/**
 * "Do it your way" (spec 53 section 15, Mark 2026-11-02): a training run
 * that gives the task and a checklist instead of one card at a time. Pure:
 * no Vue, no DOM. TaskHost.vue runs it.
 *
 *   linesOf(steps)            the checklist: one line per card (a step's
 *                             `group`) that holds a graded step
 *   watchable(step, context)  the step with the run's context filled in, or
 *                             null while a token it needs is unknown
 *   forWatching(step)         the step as the watcher waits on it: an Enter
 *                             step needs the person's own edit (`redo`)
 *   tickedLines(lines, log)   which lines every graded step of has been met
 *
 * `steps` is the flattened definition for this fleet and role (the engine's
 * flattenSteps, with the run's context), so a fleet that gets `open-driver-any`
 * instead of `open-driver` lists that one.
 */
import { fill, tokensIn } from '../tours/context';
import { isGraded } from './kinds';

/**
 * The checklist, in the definition's order. A line is a card: its title, a
 * hint made of its graded steps' tags ("Set 8:00 AM tomorrow · Click Check
 * drivers"), the ids it needs met, and whether it carries the simulated
 * driver (a `drive` step's "Send the driver").
 */
export function linesOf(steps, context = {}) {
  const lines = [];
  const byGroup = new Map();
  for (const step of Array.isArray(steps) ? steps : []) {
    const key = step.group || step.id;
    let line = byGroup.get(key);
    if (!line) {
      line = {
        key,
        title: step.title || '',
        task: step.task || '',
        chapterId: step.chapterId || null,
        firstStepId: step.id,
        stepIds: [],
        tags: [],
        drive: null,
      };
      byGroup.set(key, line);
      lines.push(line);
    }
    if (step.drive) line.drive = step;
    if (!isGraded(step)) continue;
    line.stepIds.push(step.id);
    if (step.tag) line.tags.push(fill(step.tag, context));
  }
  return lines
    .filter(line => line.stepIds.length || line.drive)
    .map(line => ({
      ...line,
      hint: line.task ? fill(line.task, context) : line.tags.join(' · '),
    }));
}

/** The parts of a step its watcher reads: where it is, and what counts. */
const WATCHED = ['target', 'until', 'act', 'capture', 'route', 'field'];

/**
 * The step filled from what the run knows, or null while a token the watcher
 * needs (the load's id in a request's path, the driver a ring names) is still
 * unknown. A step armed too early would wait on `{loadId}` written out.
 */
export function watchable(step, context = {}) {
  if (!step) return null;
  const used = tokensIn(WATCHED.map(key => step[key]));
  for (const token of used) {
    const value = context[token];
    if (value === undefined || value === null || value === '') return null;
  }
  return fill(step, context);
}

/**
 * The step as the watcher waits on it. An Enter step counts only once the
 * person has edited the control since the run began (fieldWatch's `redo`):
 * a box that already held a value when they arrived is not them doing it.
 */
export function forWatching(step) {
  if (!step || !step.until || !step.until.field) return step;
  return { ...step, until: { ...step.until, redo: true } };
}

/** Each line, with `done` when every graded step on it is in the log as met. */
export function tickedLines(lines, log = []) {
  const met = new Set(
    (Array.isArray(log) ? log : [])
      .filter(entry => entry && (entry.mode === 'self' || entry.mode === 'shown'))
      .map(entry => entry.id)
  );
  return (Array.isArray(lines) ? lines : []).map(line => ({
    ...line,
    done: line.stepIds.length > 0 && line.stepIds.every(id => met.has(id)),
    shown: line.stepIds.some(id =>
      (Array.isArray(log) ? log : []).some(e => e && e.id === id && e.mode === 'shown')
    ),
  }));
}

/** Every checklist line is done: the run can be finished. */
export function allDone(lines) {
  const graded = (Array.isArray(lines) ? lines : []).filter(line => line.stepIds.length);
  return graded.length > 0 && graded.every(line => line.done);
}
